import React, { useEffect, useRef, useState } from "react";
import { decodePolyline, loadLeaflet } from "../../../libs/leaflet";

/**
 * The day an engineer drove, drawn on a map.
 *
 * The geometry comes from Google's Routes API, computed server-side and handed
 * over as an encoded polyline — so the line follows real roads rather than
 * cutting across the map from pin to pin. The API key stays on the server, so
 * the tiles underneath are OpenStreetMap's (the same source the Map View screen
 * uses) and only the route itself is Google's.
 *
 * When the route could not be computed — no key, a stop with no coordinates,
 * more stops than the API allows — `encodedPolyline` is null and the component
 * falls back to straight lines between the stops, with a note saying so. A
 * degraded map beats no map: the pins are still where the engineer was.
 */

export interface RouteMapStop {
  /** Marker label: "S" / "E" for the ends, the stop number for the sites. */
  badge: string;
  title: string;
  subtitle?: string | null;
  latitude: number | null;
  longitude: number | null;
  kind: "start" | "site" | "end";
}

interface RouteMapProps {
  stops: RouteMapStop[];
  /** Google's road geometry for the whole journey; null falls back to straight lines. */
  encodedPolyline: string | null;
  /** Straight-line path through the stops, used when there is no polyline. */
  fallbackPath: Array<[number, number]>;
  /** Redraw trigger — the modal keeps one map and swaps routes on it. */
  routeKey: string;
  heightClass?: string;
}

const COLORS: Record<RouteMapStop["kind"], string> = {
  start: "#15803d", // green — where the day began
  site: "#1d4ed8", // blue — a site
  end: "#b91c1c", // red — where the day finished
};

const RouteMap: React.FC<RouteMapProps> = ({
  stops,
  encodedPolyline,
  fallbackPath,
  routeKey,
  heightClass = "h-72",
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled && !(window as any).L) setFailed(true);
    }, 8000);
    loadLeaflet(() => {
      if (cancelled) return;
      window.clearTimeout(timer);
      setReady(true);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  // Tear the map down with the component: the modal unmounts it, and Leaflet
  // leaves the container marked as initialised if it is not removed.
  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!ready || !divRef.current) return;
    const L = (window as any).L;
    if (!L) return;

    const points = stops.filter(
      (s) => s.latitude !== null && s.longitude !== null,
    ) as Array<RouteMapStop & { latitude: number; longitude: number }>;

    if (!mapRef.current) {
      mapRef.current = L.map(divRef.current, {
        center: points.length > 0 ? [points[0].latitude, points[0].longitude] : [20.5937, 78.9629],
        zoom: points.length > 0 ? 10 : 5,
        scrollWheelZoom: false,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }

    layerRef.current.clearLayers();

    const road = decodePolyline(encodedPolyline);
    const line = road.length > 1 ? road : fallbackPath;
    if (line.length > 1) {
      L.polyline(line, {
        color: "#1d4ed8",
        weight: 4,
        opacity: 0.85,
        // A straight-line fallback is dashed, so nobody reads it as a road.
        dashArray: road.length > 1 ? undefined : "6 6",
      }).addTo(layerRef.current);
    }

    points.forEach((stop) => {
      const color = COLORS[stop.kind];
      const icon = L.divIcon({
        className: "",
        html: `<div style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:${color};color:#fff;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);">${stop.badge}</div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -14],
      });
      const marker = L.marker([stop.latitude, stop.longitude], { icon }).addTo(layerRef.current);
      const esc = (v: string) =>
        v.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch] as string);
      marker.bindPopup(
        `<div style="font-size:12px;max-width:220px;">
           <div style="font-weight:600;margin-bottom:2px;">${esc(stop.title)}</div>
           ${stop.subtitle ? `<div style="color:#4b5563;">${esc(stop.subtitle)}</div>` : ""}
           <div style="color:#6b7280;margin-top:2px;font-family:monospace;">${stop.latitude.toFixed(6)}, ${stop.longitude.toFixed(6)}</div>
         </div>`,
        { maxWidth: 260 },
      );
    });

    const bounds = line.length > 1 ? line : points.map((s) => [s.latitude, s.longitude] as [number, number]);
    if (bounds.length > 0) {
      mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }

    // The modal animates in, so the container has no size on the first paint.
    const t = window.setTimeout(() => mapRef.current?.invalidateSize(), 180);
    return () => window.clearTimeout(t);
  }, [ready, stops, encodedPolyline, fallbackPath, routeKey]);

  const plottable = stops.filter((s) => s.latitude !== null && s.longitude !== null).length;

  if (failed) {
    return (
      <div className={`${heightClass} flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-sm text-gray-500`}>
        The map could not be loaded. The route table below still shows every stop.
      </div>
    );
  }

  if (plottable === 0) {
    return (
      <div className={`${heightClass} flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-sm text-gray-500`}>
        No coordinates were recorded for this route, so there is nothing to plot.
      </div>
    );
  }

  return (
    <div className="relative">
      <div ref={divRef} className={`${heightClass} w-full rounded border border-gray-200 z-0`} />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center rounded bg-gray-50/80 text-sm text-gray-500">
          Loading map…
        </div>
      )}
    </div>
  );
};

export default RouteMap;
